// Word Garden — the flower BED: many plants growing together in one patch of soil.
//
// Two surfaces, one renderer. The finish screen shows the handful of words that grew
// this session; clicking that bed opens the whole garden. They are the same picture at
// two sizes, which is the point — the bed you meet after a drill is literally a corner
// of the garden you own. (Prototype: docs/design/garden/garden-vista-mockup.html.)
//
// Canvas, not DOM: a garden is thousands of plants eventually, and thousands of <svg>
// nodes with gradients and drop-shadow filters is not a thing a browser enjoys. Each
// stage is rasterized ONCE from the injected sprite into an offscreen bitmap, then
// blitted; a full repaint of a large bed costs a few milliseconds.
//
// Depends on garden.js (the sprite host + stage names + humanizeDays) and PL.layers
// (Esc / outside-press dismissal for the overlay).

window.GARDEN_BED = (function () {
	"use strict";

	var GOLDEN_ANGLE = 2.39996322972865;

	// The sprite's shared frame, cropped at the soil line (the mound's apex, y=84.32):
	// bed plants rise out of the painted ground, so they must not each carry their own
	// pile. #g-collar lives entirely below that line, so the crop drops it for free.
	var FRAME_W = 80, FRAME_H = 98;
	var FIELD_H = 82.2, FIELD_VB = "24 2 80 82.2";

	// Where each stage's "head" sits, as a fraction of stamp height measured UP from the
	// base — the hover anchor, so you aim at the thing that reads as the plant (the bloom
	// on 4-7, the stem tip on germ/1-3) rather than at bare stem. Read off the sprite's
	// geometry via (84.2 - y) / FIELD_H; re-derive if the plant art changes height.
	// 0 (the v2 sprout) shares germ's own head anchor — same provisional art, same geometry.
	var HEAD_F = { germ: .17, 0: .17, 1: .29, 2: .33, 3: .42, 4: .50, 5: .56, 6: .55, 7: .55 };

	// Nominal plant height in px at mid-depth. CONSTANT across both surfaces — the bed
	// grows by covering more ground, never by shrinking its flowers.
	var PLANT_H = 54;
	// Soil half-width px per sqrt(plant): area ∝ n at constant plant size ⇒ constant
	// density, so adding words spreads the bed outward and never crowds it.
	var UNIT = 33;
	var RATIO = 0.52;                 // soil height / width — a shallow ellipse, seen at an angle
	// Plant BASES are confined to this fraction of the soil ellipse. Under 1 by a real
	// margin because a base exactly on the rim puts the plant's whole body over the edge —
	// which is how seedlings ended up floating outside the bed. Every base now lands well
	// inside the soil, and the headroom below reserves the room their foliage needs above it.
	var PLANT_INSET = 0.78;
	var SIDE_PAD = 10;                // canvas margin either side of the soil, for rim shadow

	var kindImg = {}, dryCache = {}, dropImg = null, rasterizing = false, rasterReady = false;
	var rasterWaiters = [];

	// ── deterministic PRNG (mulberry32) ──────────────────────────────────────────
	// The jitter that keeps the spiral from reading as a machine-planted grid must be
	// STABLE: a word has to sit in the same spot every time you open the garden, or the
	// bed stops feeling like a place. Seeded by plant index, never by Math.random().
	function mulberry32(a) {
		return function () {
			a |= 0; a = a + 0x6D2B79F5 | 0;
			var t = Math.imul(a ^ a >>> 15, 1 | a);
			t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
			return ((t ^ t >>> 14) >>> 0) / 4294967296;
		};
	}

	function esc(s) {
		return String(s == null ? "" : s)
			.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
			.replace(/"/g, "&quot;").replace(/'/g, "&#39;");
	}

	// ⚠️ Mirrors garden.js's OWN bodyId — read the reasoning there. In short: "g-germ-body" is
	// STAGE 0's art (the v2 sprout) and the "germ" KIND is Ready, which draws "g-ready-body";
	// the two stopped sharing one drawing with the owner's 2026-08-25 art pick. This file keeps
	// its own copy because rasterization needs a plain function it can call per stage while baking
	// bitmaps, not garden.js's DOM-building plantSvg — so any change here has to land in both.
	function bodyId(kind) {
		return kind === 0 ? "g-germ-body"
			: (kind === "germ" || kind === "ready") ? "g-ready-body" : "g-plant-" + kind;
	}

	// ── rasterize every stage once ───────────────────────────────────────────────
	// Chrome re-rasterizes an SVG-backed Image on EVERY drawImage, so the Image is baked
	// into a canvas first and the canvas is what gets blitted.
	function bake(img, filter) {
		var c = document.createElement("canvas");
		c.width = img.width; c.height = img.height;
		var cx = c.getContext("2d");
		if (filter) { cx.filter = filter; }
		cx.drawImage(img, 0, 0);
		return c;
	}

	// The parametric wither's dry tint, for canvas stamps: the degree is quantized UP to
	// quarter steps and each (stage, step) variant is baked once from the fresh stamp on
	// first need — a big overdue garden costs at most 4 extra bitmaps per stage, not one
	// per plant. The droop half of the wither is NOT baked: rotation is a cheap per-blit
	// context transform, and staying continuous there keeps neighbouring plants from
	// sharing one identical lean. (In the bed the whole plant tips — stem, leaves and all —
	// which for a soil-line-cropped stamp pivoting at its base reads the same as the
	// single-plant SVG's body-only droop.)
	function dryImg(kind, d) {
		var b = Math.min(1, Math.ceil(d * 4) / 4);
		if (!(b > 0)) { return kindImg[kind]; }
		var key = kind + "|" + b;
		if (!dryCache[key] && kindImg[kind]) {
			dryCache[key] = bake(kindImg[kind], GARDEN.witherRender(b).filter);
		}
		return dryCache[key];
	}

	function rasterize(done) {
		if (rasterReady) { done(); return; }
		rasterWaiters.push(done);
		if (rasterizing) { return; }
		rasterizing = true;

		var defs = GARDEN.spriteDefs();
		if (!defs) {   // sprite still in flight — retry on the next tick rather than paint nothing
			rasterizing = false;
			setTimeout(function () { var w = rasterWaiters.splice(0); w.forEach(rasterize); }, 120);
			return;
		}
		// 0 (the v2 sprout) and "germ" (Ready) are two DIFFERENT drawings since 2026-08-25, and
		// each is baked under its own kindImg key — as they already were while the art was shared,
		// so dryImg/kindImg stay simple "index by whatever kind the plant object carries" lookups
		// everywhere else in this file and the art split needed no change here.
		var kinds = ["germ", 0, 1, 2, 3, 4, 5, 6, 7], pending = kinds.length + 1;
		function finish() {
			if (--pending) { return; }
			rasterReady = true;
			rasterWaiters.splice(0).forEach(function (fn) { fn(); });
		}
		function mk(svgText, cb) {
			var img = new Image();
			img.onload = function () { cb(img); finish(); };
			img.onerror = finish;   // one missing stamp must not strand the whole bed
			img.src = URL.createObjectURL(new Blob([svgText], { type: "image/svg+xml" }));
		}
		kinds.forEach(function (k) {
			mk('<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"'
				+ ' viewBox="' + FIELD_VB + '" width="' + FRAME_W * 2 + '" height="' + FIELD_H * 2 + '">'
				+ defs + '<use xlink:href="#' + bodyId(k) + '" href="#' + bodyId(k) + '"/></svg>',
				function (img) {
					// Overdue variants (the same plant, drying out — never different art, a
					// stage NEVER drops) are baked lazily from this by dryImg() above.
					kindImg[k] = bake(img);
				});
		});
		mk('<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"'
			+ ' viewBox="0 0 24 24" width="48" height="48">' + defs
			+ '<use xlink:href="#g-droplet" href="#g-droplet"/></svg>',
			function (img) { dropImg = bake(img); });
	}

	// ── bed geometry ─────────────────────────────────────────────────────────────
	// Small when the garden is young, wider as it fills — "organic" means the PATCH grows,
	// not the plants. Everything is derived from the soil ellipse: the canvas is sized to
	// CONTAIN it plus the headroom the tallest plant needs above it, rather than the soil
	// being fitted into a canvas picked first. That inversion is what keeps plants on soil.
	function geometry(n, minW, maxW, reach) {
		var w = Math.round(2 * UNIT * Math.sqrt(Math.max(n, 4))) + 2 * SIDE_PAD;
		w = Math.max(minW, Math.min(maxW, w));
		var srx = (w - 2 * SIDE_PAD) / 2;
		var sry = srx * RATIO;
		// Room above the soil for the tallest plant standing at the BACK rim, plus whatever
		// spills past its own box (the hover scale-up, a promoted plant's glow).
		//
		// Measured off the plants actually in this bed rather than assumed (owner report
		// 2026-08-13: "reduce the space between the top of the flowers and Today's growth").
		// The old figure reserved 1.30 × PLANT_H unconditionally — the sky a stage-7 flower
		// standing at the FRONT rim would need, which is a plant that by construction is not
		// at the top of the picture. A bed of three sprouts got the same 78px of empty air,
		// and on the finish screen that air sits right under the heading. `reach` (see
		// topReach) is the tallest thing this bed actually draws, as a fraction of a plant's
		// stamp; back-rim plants are drawn at the small end of the depth scale (0.78), so
		// that is what the reservation is measured at.
		var headroom = Math.round(PLANT_H * 0.78 * (reach || 1.30) + 8);
		return {
			w: w,
			h: Math.round(headroom + 2 * sry + SIDE_PAD),
			cx: w / 2,
			cy: headroom + sry,     // soil-ellipse centre
			srx: srx,
			sry: sry
		};
	}

	// How far above its own foot the tallest thing in this bed reaches, in stamp heights —
	// geometry()'s `reach`. Two things can be the tallest and they are measured separately:
	//
	//   the ART   — every stage shares one frame and fills a different part of it, so the top
	//               of the drawing sits about .30 of a stamp above the stage's HEAD anchor
	//               (read off garden.svg: the stage-7 halo tops out at .84, its head is at
	//               .55). A hovered plant draws at HOVER_SCALE from the same foot.
	//   the GLOW  — a promoted plant's halo is a radial gradient of .72 stamps around the
	//               head, transparent at the rim; .62 is where it stops being visible.
	//
	// Both are per-stage, so a bed of sprouts reserves a sprout's sky and a bed of roses a
	// rose's. The 1.30 default stands for a caller that names no plants.
	var ART_ABOVE_HEAD = .30, GLOW_F = .62;

	function bedReach(plants) {
		var max = 0;
		for (var i = 0; i < plants.length; i++) {
			var hf = HEAD_F[plants[i].kind] || .5;
			var r = (hf + ART_ABOVE_HEAD) * HOVER_SCALE;
			if (plants[i].promoted) { r = Math.max(r, hf + GLOW_F); }
			if (r > max) { max = r; }
		}
		return max || 1.30;
	}

	// Golden-angle spiral inside the soil; planting order = index, so a word keeps its spot
	// and new words land nearer the rim. r ∝ sqrt(i/n) spreads them at even density, and
	// PLANT_INSET keeps every base clear of the edge.
	function layout(n, g) {
		var rx = g.srx * PLANT_INSET, ry = g.sry * PLANT_INSET, pts = [];
		for (var i = 0; i < n; i++) {
			var rnd = mulberry32(i * 2654435761 + 13);
			// jitter clamped to <=1 so a rim plant can never be nudged off the soil
			var r = Math.min(1, Math.sqrt((i + .5) / n) + (rnd() - .5) * .07);
			var th = i * GOLDEN_ANGLE + (rnd() - .5) * .22;
			pts.push({ x: g.cx + Math.cos(th) * r * rx, y: g.cy + Math.sin(th) * r * ry });
		}
		return pts;
	}

	// ── the bed itself ───────────────────────────────────────────────────────────
	// Drawn as a raised elliptical patch on a TRANSPARENT canvas — not a filled rectangle
	// with an ellipse inside it. The old beige box read as a screenshot of a garden; a bare
	// mound reads as a garden bed sitting on the page.
	function paintSoil(ctx, g) {
		var cx = g.cx, cy = g.cy, rx = g.srx, ry = g.sry;

		ctx.save();
		// contact shadow, slightly below and narrower — grounds the mound
		ctx.fillStyle = "rgba(60,40,20,.16)";
		ctx.filter = "blur(6px)";
		ctx.beginPath();
		ctx.ellipse(cx, cy + ry * .30, rx * .94, ry * .60, 0, 0, Math.PI * 2);
		ctx.fill();
		ctx.filter = "none";

		// soil body: lit from the upper left, deepening to the rim
		var soil = ctx.createRadialGradient(
			cx - rx * .28, cy - ry * .45, Math.min(rx, ry) * .12,
			cx, cy, rx);
		soil.addColorStop(0, "#C6A177");
		soil.addColorStop(.52, "#A07A52");
		soil.addColorStop(.86, "#7E5B39");
		soil.addColorStop(1, "#63452A");
		ctx.fillStyle = soil;
		ctx.beginPath();
		ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
		ctx.fill();

		// everything below is confined to the mound
		ctx.clip();

		// Raked earth: two very faint furrows. Three at double this opacity read as a
		// bullseye rather than as tilled soil — keep them barely there.
		ctx.strokeStyle = "rgba(58,38,20,.07)";
		ctx.lineWidth = 1.2;
		for (var f = 1; f <= 2; f++) {
			var k = f / 3;
			ctx.beginPath();
			ctx.ellipse(cx, cy, rx * (1 - k * .5), ry * (1 - k * .5), 0, 0, Math.PI * 2);
			ctx.stroke();
		}

		// grit: deterministic speckles, light and dark, so the soil has tooth
		var rnd = mulberry32(97);
		var specks = Math.round(rx * ry / 190);
		for (var i = 0; i < specks; i++) {
			var a = rnd() * Math.PI * 2, rr = Math.sqrt(rnd());
			var sx = cx + Math.cos(a) * rr * rx * .97;
			var sy = cy + Math.sin(a) * rr * ry * .97;
			var s = .7 + rnd() * 1.5;
			ctx.fillStyle = rnd() < .5 ? "rgba(190,155,110,.40)" : "rgba(48,30,15,.30)";
			ctx.beginPath();
			ctx.ellipse(sx, sy, s, s * .62, 0, 0, Math.PI * 2);
			ctx.fill();
		}

		// rim: a dark inner edge all round plus a lit crest along the top
		ctx.lineWidth = 3;
		ctx.strokeStyle = "rgba(45,28,14,.30)";
		ctx.beginPath();
		ctx.ellipse(cx, cy, rx - 1, ry - 1, 0, 0, Math.PI * 2);
		ctx.stroke();
		ctx.lineWidth = 2;
		ctx.strokeStyle = "rgba(226,196,150,.45)";
		ctx.beginPath();
		ctx.ellipse(cx, cy, rx - 2, ry - 2, 0, Math.PI * 1.12, Math.PI * 1.88);
		ctx.stroke();

		ctx.restore();
	}

	// ── promoted plants: the glow ────────────────────────────────────────────────
	// The words that grew this session are picked out INSIDE the session's bed (design §6)
	// rather than getting a bed of their own. The bed is ONE canvas — a garden is thousands
	// of plants and a DOM node per plant is not an option — so this cannot be a CSS keyframe
	// on an element the way the deny-shake is; the pulse drives the canvas repaint instead.
	// Which is also why honouring prefers-reduced-motion is trivial here: pin the phase and
	// never start the loop, and the same code paints a static highlight.
	var PULSE_MS = 2400;

	// A hovered plant grows in place — same base, same neighbours, just easier to read.
	// Triggered by a direct canvas hover AND externally (setHoverTerm), so the finish
	// screen's "Grew today" word list and its flower read as one linked picture.
	var HOVER_SCALE = 1.32;

	// `k` is the pulse phase, 0..1. The plant is LIT, never moved or scaled: a plant that
	// grows on the spot would have to push its neighbours around, and the bed's layout is
	// the one thing that must stay stable (a word keeps its place in the garden).
	function paintGlow(ctx, p, k) {
		var headY = p.y - p.h * (HEAD_F[p.p.kind] || .5);
		var rad = Math.max(16, p.h * (.60 + .12 * k));
		var a = .34 + .32 * k;
		var grad = ctx.createRadialGradient(p.x, headY, rad * .12, p.x, headY, rad);
		grad.addColorStop(0, "rgba(255,232,150," + a.toFixed(3) + ")");
		grad.addColorStop(.5, "rgba(255,208,96," + (a * .45).toFixed(3) + ")");
		grad.addColorStop(1, "rgba(255,196,72,0)");
		ctx.save();
		ctx.fillStyle = grad;
		ctx.beginPath();
		ctx.arc(p.x, headY, rad, 0, Math.PI * 2);
		ctx.fill();
		// Lit soil at the foot, plus a bright rim on it — this is what grounds the glow (it
		// reads as this plant's own light, not as haze floating over the bed) and it is also
		// exactly the "static highlight ring" the reduced-motion fallback needs, so there is
		// one drawing, not two.
		ctx.fillStyle = "rgba(255,216,116," + (.20 + .22 * k).toFixed(3) + ")";
		ctx.beginPath();
		ctx.ellipse(p.x, p.y, p.w * .50, p.h * .13, 0, 0, Math.PI * 2);
		ctx.fill();
		ctx.strokeStyle = "rgba(255,236,168," + (.30 + .30 * k).toFixed(3) + ")";
		ctx.lineWidth = 1.6;
		ctx.stroke();
		ctx.restore();
	}

	function reducedMotion() {
		return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
	}

	// ── one bed instance ─────────────────────────────────────────────────────────
	// `plants`: [{termId, word, kind, overdue, dueDays, note?, promoted?, unplanted?}] —
	// `note` is the extra line the finish bed uses to say what a word grew FROM, `promoted`
	// marks a word that grew THIS session (it glows), `unplanted` a word that was drilled
	// but has no plant behind it yet (no watering date to promise).
	function Bed(canvas, plants, opts) {
		var ctx = canvas.getContext("2d");
		var dpr = Math.min(window.devicePixelRatio || 1, 2);
		var g = geometry(plants.length, opts.minW, opts.maxW, bedReach(plants));
		var W = g.w, H = g.h;
		var draw = [], hovered = -1, hoverTimer = null;
		var anyPromoted = false;
		for (var q = 0; q < plants.length; q++) { if (plants[q].promoted) { anyPromoted = true; } }
		// Motion off → the phase is pinned near the top of the pulse, which paints exactly the
		// static highlight ring the design asks for; the rAF loop below never starts.
		var pulse = reducedMotion() ? .62 : 0;
		var pulseT0 = null;

		canvas.width = W * dpr;
		canvas.height = H * dpr;
		canvas.style.width = W + "px";
		canvas.style.height = H + "px";

		function build() {
			var pts = layout(plants.length, g);
			draw = [];
			for (var i = 0; i < plants.length; i++) {
				// Depth scale: plants nearer the front (larger y) are drawn bigger, which
				// with the painter's-order sort below is the whole 3D of this picture.
				var yn = Math.max(0, Math.min(1, (pts[i].y - (g.cy - g.sry)) / (2 * g.sry)));
				var h = PLANT_H * (.78 + .40 * yn);
				draw.push({
					i: i, x: pts[i].x, y: pts[i].y,
					w: h * FRAME_W / FIELD_H, h: h, p: plants[i]
				});
			}
			draw.sort(function (a, b) { return a.y - b.y; });   // back to front
		}

		function render() {
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
			ctx.clearRect(0, 0, W, H);   // transparent — the mound IS the picture
			paintSoil(ctx, g);

			for (var j = 0; j < draw.length; j++) {
				var p = draw[j];
				// The hovered plant draws at HOVER_SCALE, base pinned to p.y (its soil-line
				// foot) — it grows upward on the spot, never shifting neighbours.
				var sc = p.i === hovered ? HOVER_SCALE : 1;
				var pw = p.w * sc, ph = p.h * sc;
				if (p.i === hovered) {                        // soft ring marks the aimed-at plant
					ctx.fillStyle = "rgba(94,58,154,.22)";
					ctx.beginPath();
					ctx.ellipse(p.x, p.y, pw * .62, ph * .16, 0, 0, Math.PI * 2);
					ctx.fill();
				}
				if (p.p.promoted) { paintGlow(ctx, p, pulse); }
				// The stamp is cropped AT the soil line, so its bottom edge already IS the
				// ground contact point — it needs no vertical shim.
				var wd = GARDEN.witherDegree(p.p);
				var img = wd > 0 ? dryImg(p.p.kind, wd) : kindImg[p.p.kind];
				if (img) {
					var wr = wd > 0 ? GARDEN.witherRender(wd) : null;
					if (wr && wr.rot) {
						// droop: tip the plant over its foot (the ground contact point)
						ctx.save();
						ctx.translate(p.x, p.y);
						ctx.rotate(wr.rot * Math.PI / 180);
						ctx.drawImage(img, -pw / 2, -ph, pw, ph);
						ctx.restore();
					} else {
						ctx.drawImage(img, p.x - pw / 2, p.y - ph, pw, ph);
					}
				}
				if (p.p.overdue && dropImg) {
					// Top-LEFT of the plant's HEAD, matching the single-plant surfaces: the bloom
					// leans right, so the left shoulder is the empty corner. Anchored to the head
					// (HEAD_F), never to the stamp's top edge — every stage shares one frame sized
					// for a stage-7 flower, so a sprout fills only its lowest third and a
					// frame-anchored droplet floated far above the plant, out in open sky.
					// Offsets are in droplet-widths, not frame-widths, for the same reason.
					var d = Math.max(9, ph * .17);
					var headY = p.y - ph * (HEAD_F[p.p.kind] || .5);
					ctx.drawImage(dropImg, p.x - d * 1.25, headY - d * .95, d, d);
				}
			}
		}

		// ── hover peek ───────────────────────────────────────────────────────────
		// Sweeping the cursor across a large bed must cost nothing, so mousemove only
		// hides the popup and re-arms a timer; the hit test runs when the cursor RESTS.
		function hit(ev) {
			var r = canvas.getBoundingClientRect();
			var mx = (ev.clientX - r.left) * W / r.width, my = (ev.clientY - r.top) * H / r.height;
			var best = -1, bestD = Infinity;
			for (var j = 0; j < draw.length; j++) {
				var p = draw[j];
				var dx = mx - p.x, dy = my - (p.y - p.h * (HEAD_F[p.p.kind] || .5));
				var d = dx * dx + dy * dy, reach = Math.max(12, p.h * .40);
				// reach only gates "am I pointing at a plant at all?"; among candidates the
				// nearest head wins, and <= lets the FRONT plant take a tie.
				if (d < reach * reach && d <= bestD) { bestD = d; best = p.i; }
			}
			return best;
		}

		function settle(ev) {
			var best = hit(ev);
			if (best !== hovered) {
				hovered = best;
				render();
				// Tells the caller which plant is under the mouse (or none) — the finish
				// screen uses this to bold the matching "Grew today" word.
				if (opts.onHover) { opts.onHover(best >= 0 ? plants[best].termId : null); }
			}
			canvas.style.cursor = (best >= 0 && opts.onPlantClick) ? "pointer" : "";
			if (best < 0) { hidePeek(); return; }
			showPeek(plants[best], ev.clientX, ev.clientY);
		}

		canvas.addEventListener("mousemove", function (ev) {
			if (hoverTimer) { clearTimeout(hoverTimer); }
			hidePeek();
			hoverTimer = setTimeout(function () { settle(ev); }, 80);
		});
		canvas.addEventListener("mouseleave", function () {
			if (hoverTimer) { clearTimeout(hoverTimer); hoverTimer = null; }
			hidePeek();
			canvas.style.cursor = "";
			if (hovered !== -1) {
				hovered = -1;
				render();
				if (opts.onHover) { opts.onHover(null); }
			}
		});

		// A plant click, where the caller wants one (both the vista and the finish bed pass
		// onPlantClick now). Same rest-independent hit test as the hover. Hitting a plant stops
		// the click from bubbling — the finish bed also has a click handler on its WRAPPING div
		// that opens the whole vista, and that must fire only for a press on empty soil, not on
		// a plant this handler already routed to its own rich entry.
		if (opts.onPlantClick) {
			canvas.addEventListener("click", function (ev) {
				var best = hit(ev);
				if (best >= 0) { ev.stopPropagation(); opts.onPlantClick(plants[best]); }
			});
		}

		// The pulse loop, only when there is something to pulse and motion is welcome. It stops
		// itself once the canvas leaves the document — the finish screen is torn down the moment
		// the user presses Practice Again or Back to Words, and nothing else would cancel it.
		function animate(ts) {
			if (!canvas.isConnected) { return; }
			if (pulseT0 == null) { pulseT0 = ts; }
			pulse = .5 - .5 * Math.cos(((ts - pulseT0) % PULSE_MS) / PULSE_MS * 2 * Math.PI);
			render();
			requestAnimationFrame(animate);
		}

		build();
		rasterize(function () {
			render();
			if (anyPromoted && !reducedMotion()) { requestAnimationFrame(animate); }
		});

		// External hover linkage — driven by the "Grew today" word list, not the canvas
		// mouse. Rings + enlarges the matching plant exactly like a direct hover would, and
		// pops the SAME peek, anchored on the plant itself (not the word) so it reads as
		// "hovering the flower" wherever the hover actually started. Deliberately does NOT
		// call opts.onHover — that callback bolds the word FROM a canvas hover, and a word
		// hovering itself needs no round trip (see .exGrewWordClass:hover in exercise.css).
		this.setHoverTerm = function (termId) {
			var idx = -1;
			for (var k = 0; k < plants.length; k++) {
				if (String(plants[k].termId) === String(termId)) { idx = k; break; }
			}
			if (idx === hovered) { return; }
			hovered = idx;
			render();
			if (idx < 0) { hidePeek(); return; }
			var d = null;
			for (var m = 0; m < draw.length; m++) { if (draw[m].i === idx) { d = draw[m]; break; } }
			if (d) {
				var r = canvas.getBoundingClientRect();
				var sx = r.left + d.x * r.width / W;
				var sy = r.top + (d.y - d.h * (HEAD_F[d.p.kind] || .5)) * r.height / H;
				showPeek(plants[idx], sx, sy);
			}
		};

		this.clearHoverTerm = function () {
			if (hovered === -1) { return; }
			hovered = -1;
			render();
			hidePeek();
		};

		this.repaint = function () { build(); render(); };
		this.width = W;
	}

	// ── the shared peek popup ────────────────────────────────────────────────────
	// One element on <body> reused by every bed (a garden can hold thousands of plants;
	// a node per plant is not an option) — since PR 5/7 of the tooltip engine that element
	// belongs to PL.tip's `peek` surface (design §4.1), which also owns the placement and
	// the hide-on-scroll this file used to wire by hand. The chrome class and its
	// shown-state class are unchanged (.gardenBedPeek / .gardenPeekOnClass).
	var peek = null;

	function showPeek(p, cx, cy) {
		// An unplanted word (drilled, first-ever miss → no plant written) has no stage and no
		// watering date; say so once and show no schedule line rather than promising one.
		var stageLine = p.unplanted
			? "<b>" + esc(getRes("garden_not_planted")) + "</b>"
			: p.kind === "germ"
			? "<b>Ready to grow</b>"
			: "<b>" + esc(GARDEN.stageName(p.kind)) + "</b> · stage " + p.kind + "/7";
		var due = p.unplanted ? ""
			: p.overdue
			// Under a day late reads "due today", not "overdue" — the same wording the row's
			// tooltip and its blank badge use (garden.js shortDays), so the vista and the list
			// never disagree about whether a word is late yet.
			? '<span class="gardenBedPeekDue">'
				+ (p.overdueDays >= 1 ? "overdue " + esc(GARDEN.humanizeDays(p.overdueDays)) : "due today")
				+ " — needs water</span>"
			: esc(strFormat(getRes("garden_grow_next"), GARDEN.humanizeDays(p.dueDays)));
		var html = GARDEN.plantSvg(p.kind, "pl-plant-lg", true, GARDEN.witherDegree(p))
			+ (p.overdue ? GARDEN.dropletSvg() : "")
			+ '<div class="gardenBedPeekWord">' + esc(p.word) + "</div>"
			+ (p.note ? '<div class="gardenBedPeekLine">' + esc(p.note) + "</div>" : "")
			+ '<div class="gardenBedPeekLine">' + stageLine + "</div>"
			+ (due ? '<div class="gardenBedPeekLine">' + due + "</div>" : "")
			// only clickable plants advertise the click (the finish bed's don't — there the
			// whole bed is one button and the hint would promise the wrong thing)
			+ (p.nav ? '<div class="gardenBedPeekLine gardenBedPeekHintClass">'
				+ esc(getRes("garden_vista_click_hint")) + "</div>" : "");

		// The plant is painted on a canvas, so the anchor is the POINT the cursor rests on
		// rather than an element — which is exactly what peekAt() is for. Off that point the
		// peek hugs the same top-right corner the word-list row peek does (PL.tip.place's
		// "corner-tr"), flipping per axis at the viewport edges. A wider gap: here the
		// cursor itself is the anchor.
		peek = PL.tip.peekAt({ x: cx, y: cy }, html, {
			boxClass: "gardenBedPeek", onClass: "gardenPeekOnClass",
			sides: ["corner-tr"], gap: 14, margin: 6
		});
	}

	// The pointer leaving the plant (a canvas mousemove, not a mouseleave) is this file's
	// own business; scroll is the engine's — a point-anchored peek asked to be watched.
	function hidePeek() {
		if (peek) { peek.hide(); }
	}

	// ── public: mount a bed into an element ──────────────────────────────────────
	function mount(el, plants, opts) {
		opts = opts || {};
		var canvas = document.createElement("canvas");
		canvas.className = "gardenBedCanvas";
		el.appendChild(canvas);
		return new Bed(canvas, plants, {
			minW: opts.minW || 200,
			maxW: opts.maxW || 760,
			onPlantClick: opts.onPlantClick,
			onHover: opts.onHover
		});
	}

	// ── public: the whole-garden overlay ─────────────────────────────────────────
	var vistaLayer = null;

	// The vista is the garden of the language being LEARNED — the pair's "to" side, read the same
	// way every other module reads it. Without the filter the bed drew plants from every language
	// at once, so a user who had just watered all their thirsty German words still met droplets
	// (English ones) and no way to clear them: everything the vista leads to is pair-scoped.
	// No language selected yet (a first visit) still opens the overlay — on its "nothing planted
	// yet" invitation, which is the discovery moment the My Progress door exists for.
	function openVista() {
		if (vistaLayer) { return; }
		var termLang = $("#langToValId").attr("data-lang-code");
		if (!termLang) { showVista([]); return; }
		$.ajax("api/garden/all", { data: { termLang: termLang }, dataType: "json" })
			.done(function (data) { showVista((data && data.terms) || []); })
			.fail(function () { /* the bed is a reward, not a workflow — stay quiet */ });
	}

	function showVista(terms) {
		var plants = terms.map(function (t) {
			return {
				termId: t.termId, word: t.term, kind: t.stage,
				overdue: !!t.overdue, dueDays: t.dueDays,
				// the parametric wither (GARDEN.witherDegree) reads its degree off these two
				overdueDays: t.overdueDays, intervalDays: t.intervalDays,
				nav: true   // vista plants click through to the dictionary; the peek says so
			};
		});

		var $overlay = $('<div class="gardenVistaOverlay"></div>');
		var $card = $('<div class="gardenVistaCard" role="dialog" aria-modal="true"></div>');
		// The head wears the SAME flower as the two doors that open it — My Progress's "My Garden"
		// button and the My Garden word-list row — so the picture the user clicked is the picture
		// that greets them. (It used to be GARDEN.markIcon("water"), the 💧 that means "thirsty":
		// a per-word state, not the name of the place.)
		var $head = $('<div class="gardenVistaHead"></div>')
			.append($('<img class="gardenVistaMark" src="/image/garden/tab-flower.svg" alt="">'))
			.append($('<span class="gardenVistaTitle"></span>').text(getRes("garden_vista_title")))
			.append($('<span class="gardenVistaCount"></span>').text(
				strFormat(getRes("garden_vista_count"), fmtNum(plants.length))));
		var $close = $('<button type="button" class="pl-btn-icon-round gardenVistaClose" aria-label="Close">'
			+ '<span class="pl-ic pl-ic-x"></span></button>');
		// "How does my garden grow?" — the permanent door to the explainer panel (GARDEN.openGrowthGuide,
		// docs/project/garden-growth-explainer.md). Unconditional and stateless: unlike the finish
		// screen's one-time card it is a reference the user comes BACK to, so it reads no seen-flag and
		// sets none. A plain "?" glyph, the same affordance the tabs row's help hub wears.
		var $guide = $('<button type="button" class="pl-btn-icon-round gardenVistaGuide">?</button>')
			.assignTooltip("garden_guide_open_chip");
		$guide.on("click", function () { GARDEN.openGrowthGuide(); });
		var $bed = $('<div class="gardenVistaBed"></div>');

		$card.append($head.append($guide).append($close)).append($bed);
		$overlay.append($card).appendTo(document.body);

		if (plants.length) {
			// The overlay may host a big garden — let it use most of the viewport, but the
			// bed still starts small and only reaches that ceiling once it has the plants.
			mount($bed[0], plants, {
				minW: 220,
				maxW: Math.min(1160, Math.round(window.innerWidth * 0.82)),
				// A plant click opens its rich entry in a popup STACKED ON the vista (superseding
				// the earlier "walk through to the Dictionary tab" decision) — the vista stays
				// open underneath, so Esc closes the popup first and lands back on the garden,
				// and a second Esc then closes the vista too.
				// …but NOT while the guided walk is escorting: the vista is reachable from the
				// finish screen's lit bed, and the walk stands its wall down for this overlay
				// (cardLive), so this was the second door onto a rich entry mid-tour — the first
				// being the bed's own plant click, sealed in garden.js for the same owner call
				// (2026-09-01, "NO ALLOWED CLICK ON RICH VIEW"). The plant's hover peek already
				// names the word, and it stays up: the pointer is answered, nothing opens.
				onPlantClick: function (p) {
					if (window.OB_GUIDE && OB_GUIDE.armed()) { return; }
					if (window.openTermRichPopup) { window.openTermRichPopup(p.word); }
				}
			});
		} else {
			$bed.append($('<div class="gardenVistaEmpty"></div>').text(getRes("garden_vista_empty")));
		}

		vistaLayer = PL.layers.push({
			el: $card[0],
			onClose: function () {
				hidePeek();
				$overlay.remove();
				vistaLayer = null;
			}
		});
		$close.on("click", function () { if (vistaLayer) { vistaLayer.close("api"); } });
	}

	return {
		mount: mount,
		openVista: openVista,
		hidePeek: hidePeek
	};
})();
